import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Minus, Plus, Check, ArrowRight, ArrowLeft, Flower2, Calendar, Clock, User, FileText, CreditCard, PartyPopper, Copy, QrCode, Building2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useFetch } from "../lib/useData";
import { useSettings } from "../context/SettingsContext";
import { Section } from "../components/public/PublicLayout";

const money = (s, v) => `${s?.currency_symbol || "₹"}${v}`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const STEPS = ["Seva", "Date", "Time", "Details", "Review", "Payment", "Confirm"];

const UTR_RULES = {
  upi:  { re: /^\d{12}$/, msg: "UPI transaction ID exactly 12 digits ki honi chahiye" },
  qr:   { re: /^\d{12}$/, msg: "UPI transaction ID exactly 12 digits ki honi chahiye" },
  bank: { re: /^[A-Z0-9]{12,22}$/, msg: "Bank reference 12 se 22 letters/numbers ki honi chahiye" },
};

function next7days() {
  const arr = [];
  for (let i = 0; i < 21; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    arr.push(d.toISOString().slice(0, 10));
  }
  return arr;
}

export default function BookingWizard() {
  const s = useSettings();
  const [params] = useSearchParams();
  const { data: sevas } = useFetch("/public/sevas");
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    seva_id: params.get("seva") || "", date: "", time_slot: "",
    devotee_name: "", phone: "", persons: 1, email: "", address: "", gotra: "", special_request: "",
  });
  const [slots, setSlots] = useState([]);
  const [slotLoading, setSlotLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [payMethod, setPayMethod] = useState("upi");
  const [payRef, setPayRef] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const seva = (sevas || []).find((x) => x.id === form.seva_id);
  const slotInfo = slots.find((x) => x.time === form.time_slot);
  const maxPersons = Math.max(1, Math.min(10, slotInfo?.remaining ?? 10));
  const total = seva?.price || 0;
  const payRefClean = payRef.trim().toUpperCase();
  const payRefValid = UTR_RULES[payMethod].re.test(payRefClean);
  const payRefError = payRef && !payRefValid ? UTR_RULES[payMethod].msg : "";

  useEffect(() => { if (params.get("seva")) setStep(1); }, [params]);

  useEffect(() => { if (params.get("seva")) setStep(1); }, [params]);

  const loadSlots = async (date) => {
    setSlotLoading(true);
    try {
      const { data } = await api.get(`/public/availability/${form.seva_id}?date=${date}`);
      setSlots(data.slots || []);
    } catch { setSlots([]); } finally { setSlotLoading(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 0) return !!form.seva_id;
    if (step === 1) return !!form.date;
    if (step === 2) return !!form.time_slot;
    if (step === 3) return form.devotee_name && form.phone && form.phone.length >= 10 && (!form.email || EMAIL_RE.test(form.email.trim()));
    return true;
  };

  const createBooking = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/bookings", { ...form, email: form.email.trim() || null });
      setBooking(data);
      setStep(5);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };
  const submitPayment = async () => {
    if (!payRefValid) {
      toast.error(UTR_RULES[payMethod].msg);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/bookings/submit-payment", {
        booking_id: booking.booking_id,
        payment_method: payMethod,
        payment_reference: payRefClean,
      });
      setBooking(data);
      setStep(6);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied"); };

  return (
    <Section>
      <h1 className="font-display text-3xl md:text-4xl font-extrabold text-stone-900 text-center">Online Seva Booking</h1>
      <p className="text-center text-stone-500 mt-2">Complete the steps below to book your seva</p>

      {/* Stepper */}
      <div className="flex items-center justify-center gap-1 md:gap-2 mt-8 mb-10 overflow-x-auto hide-scrollbar" data-testid="booking-stepper">
        {STEPS.map((label, i) => (
          <div key={label} className="flex items-center shrink-0">
            <div className={`flex items-center gap-2 px-2 ${i <= step ? "text-orange-700" : "text-stone-400"}`}>
              <span className={`w-8 h-8 rounded-full grid place-items-center text-sm font-bold border-2 ${i < step ? "bg-orange-600 border-orange-600 text-white" : i === step ? "border-orange-600 text-orange-700" : "border-stone-300"}`}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </span>
              <span className="hidden md:inline text-xs font-semibold">{label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`w-4 md:w-8 h-0.5 ${i < step ? "bg-orange-600" : "bg-stone-200"}`} />}
          </div>
        ))}
      </div>

      <div className="max-w-3xl mx-auto bg-white rounded-3xl border border-amber-200 shadow-sm p-6 md:p-10">
        {/* STEP 0: Select Seva */}
        {step === 0 && (
          <div data-testid="step-seva">
            <h2 className="font-display text-xl font-bold text-stone-900 mb-5 flex items-center gap-2"><Flower2 className="w-5 h-5 text-orange-600" /> Select a Seva</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {(sevas || []).map((x) => (
                <button key={x.id} onClick={() => set("seva_id", x.id)} className={`text-left p-4 rounded-2xl border-2 transition-all ${form.seva_id === x.id ? "border-orange-600 bg-amber-50" : "border-stone-200 hover:border-amber-300"}`} data-testid={`select-seva-${x.slug}`}>
                  <div className="flex gap-3">
                    <img src={x.image} alt="" className="w-16 h-16 rounded-xl object-cover" />
                    <div>
                      <div className="font-bold text-stone-900">{x.name}</div>
                      <div className="text-xs text-stone-500">{x.category_name}</div>
                      <div className="text-orange-700 font-extrabold mt-1">{money(s, x.price)}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 1: Date */}
        {step === 1 && (
          <div data-testid="step-date">
            <h2 className="font-display text-xl font-bold text-stone-900 mb-5 flex items-center gap-2"><Calendar className="w-5 h-5 text-orange-600" /> Select a Date</h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2">
              {next7days().map((d) => {
                const dt = new Date(d);
                const dayName = dt.toLocaleDateString("en", { weekday: "short" });
                const allowed = !seva?.available_days?.length || seva.available_days.includes(dt.toLocaleDateString("en", { weekday: "long" }));
                return (
                  <button key={d} disabled={!allowed} onClick={() => { set("date", d); set("time_slot", ""); loadSlots(d); }}
                    className={`p-2 rounded-xl border-2 text-center transition-all disabled:opacity-30 disabled:cursor-not-allowed ${form.date === d ? "border-orange-600 bg-amber-50" : "border-stone-200 hover:border-amber-300"}`} data-testid={`date-${d}`}>
                    <div className="text-[10px] uppercase text-stone-500">{dayName}</div>
                    <div className="font-bold text-stone-900">{dt.getDate()}</div>
                    <div className="text-[10px] text-stone-400">{dt.toLocaleDateString("en", { month: "short" })}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 2: Time */}
        {step === 2 && (
          <div data-testid="step-time">
            <h2 className="font-display text-xl font-bold text-stone-900 mb-5 flex items-center gap-2"><Clock className="w-5 h-5 text-orange-600" /> Select a Time Slot</h2>
            {slotLoading ? <p className="text-stone-500">Checking availability…</p> : slots.length === 0 ? (
              <div className="text-center py-8 text-stone-500">No slots available for this date. Please go back and choose another date.</div>
            ) : (
              <div className="grid sm:grid-cols-3 gap-3">
                {slots.map((sl) => (
                  <button key={sl.time} disabled={sl.full} onClick={() => { set("time_slot", sl.time); set("persons", Math.min(form.persons, Math.max(1, sl.remaining))); }} className={`p-4 rounded-2xl border-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed ${form.time_slot === sl.time ? "border-orange-600 bg-amber-50" : "border-stone-200 hover:border-amber-300"}`} data-testid={`slot-${sl.time.replace(/[:\s]/g, "")}`}>
                    <div className="font-bold text-stone-900">{sl.time}</div>
                    <div className={`text-xs font-semibold mt-1 ${sl.full ? "text-red-600" : "text-emerald-700"}`}>{sl.full ? "Fully booked" : `${sl.remaining} of ${sl.capacity} left`}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* STEP 3: Details */}
        {step === 3 && (
          <div data-testid="step-details">
            <h2 className="font-display text-xl font-bold text-stone-900 mb-5 flex items-center gap-2"><User className="w-5 h-5 text-orange-600" /> Devotee Details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Devotee Name *" value={form.devotee_name} onChange={(v) => set("devotee_name", v)} testid="input-name" />
              <Field label="Contact Number *" value={form.phone} onChange={(v) => set("phone", v.replace(/\D/g, "").slice(0, 10))} testid="input-phone" />
              <div className="sm:col-span-2" data-testid="persons-selector">
                <label className="block text-sm font-semibold text-stone-700 mb-1">Number of Persons *</label>
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="inline-flex items-center border border-stone-300 rounded-xl overflow-hidden">
                    <button type="button" onClick={() => set("persons", Math.max(1, form.persons - 1))} disabled={form.persons <= 1} className="px-4 py-3 hover:bg-amber-50 disabled:opacity-30" data-testid="persons-minus" aria-label="Decrease persons"><Minus className="w-4 h-4" /></button>
                    <span className="w-12 text-center font-bold text-stone-900" data-testid="persons-count">{form.persons}</span>
                    <button type="button" onClick={() => set("persons", Math.min(maxPersons, form.persons + 1))} disabled={form.persons >= maxPersons} className="px-4 py-3 hover:bg-amber-50 disabled:opacity-30" data-testid="persons-plus" aria-label="Increase persons"><Plus className="w-4 h-4" /></button>
                  </div>
                 <div className="text-sm text-stone-600" data-testid="persons-total">Amount: <strong className="text-orange-700 text-lg">{money(s, total)}</strong></div>
                </div>
                <p className="text-xs text-stone-500 mt-1">Maximum {maxPersons} person(s) for the selected time slot.</p>
              </div>
              <Field label="Email (recommended)" type="email" value={form.email} onChange={(v) => set("email", v)} testid="input-email"
                error={form.email && !EMAIL_RE.test(form.email.trim()) ? "Please enter a valid email address" : ""} />
              <Field label="Gotra (optional)" value={form.gotra} onChange={(v) => set("gotra", v)} testid="input-gotra" />
              <div className="sm:col-span-2"><Field label="Address (optional)" value={form.address} onChange={(v) => set("address", v)} testid="input-address" /></div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-stone-700 mb-1">Special Request / Sankalp (optional)</label>
                <textarea value={form.special_request} onChange={(e) => set("special_request", e.target.value)} rows={3} className="w-full rounded-xl border border-stone-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400" data-testid="input-request" />
              </div>
            </div>
          </div>
        )}

        {/* STEP 4: Review */}
        {step === 4 && (
          <div data-testid="step-review">
            <h2 className="font-display text-xl font-bold text-stone-900 mb-5 flex items-center gap-2"><FileText className="w-5 h-5 text-orange-600" /> Review Your Booking</h2>
            <div className="divide-y divide-amber-100 border border-amber-200 rounded-2xl overflow-hidden">
              {[["Seva", seva?.name], ["Category", seva?.category_name], ["Date", form.date], ["Time", form.time_slot], ["Devotee", form.devotee_name], ["Persons", form.persons], ["Contact", form.phone], ["Email", form.email || "—"], ["Gotra", form.gotra || "—"], ["Special Request", form.special_request || "—"]].map(([k, v]) => (
                <div key={k} className="flex justify-between px-5 py-3"><span className="text-stone-500">{k}</span><span className="font-semibold text-stone-900 text-right">{v}</span></div>
              ))}
              <div className="flex justify-between px-5 py-4 bg-amber-50"><span className="font-bold text-stone-900">Amount Payable</span><span className="font-extrabold text-orange-700 text-xl">{money(s, total)}</span></div>
             
            </div>
            <p className="text-xs text-stone-500 mt-3">You can go back to edit any detail before proceeding to payment.</p>
          </div>
        )}

        {/* STEP 5: Payment */}
        {step === 5 && booking && (
          <div data-testid="step-payment">
            <h2 className="font-display text-xl font-bold text-stone-900 mb-2 flex items-center gap-2"><CreditCard className="w-5 h-5 text-orange-600" /> Payment</h2>
            <p className="text-sm text-stone-500 mb-5">Booking <strong className="text-orange-700">{booking.booking_id}</strong> created. Pay {money(s, booking.amount)} and submit your reference for verification.</p>
            <div className="flex gap-2 mb-5">
            {s.payment_methods?.upi && <PayTab icon={Smartphone} label="UPI" active={payMethod === "upi"} onClick={() => { setPayMethod("upi"); setPayRef(""); }} testid="pay-upi" />}
{s.payment_methods?.qr && <PayTab icon={QrCode} label="QR Code" active={payMethod === "qr"} onClick={() => { setPayMethod("qr"); setPayRef(""); }} testid="pay-qr" />}
{s.payment_methods?.bank && <PayTab icon={Building2} label="Bank Transfer" active={payMethod === "bank"} onClick={() => { setPayMethod("bank"); setPayRef(""); }} testid="pay-bank" />}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-5">
              {payMethod === "upi" && (
                <div className="flex items-center justify-between">
                  <div><div className="text-xs text-stone-500">Pay to UPI ID</div><div className="font-bold text-lg text-stone-900">{s.upi_id}</div></div>
                  <button onClick={() => copy(s.upi_id)} className="p-2 rounded-lg bg-white border border-amber-200" data-testid="copy-upi"><Copy className="w-4 h-4" /></button>
                </div>
              )}
             {payMethod === "qr" && (
  <div className="text-center">
    <img
      src={s.qr_image_url || "/images/a4.jpeg"}
      alt="Payment QR Code"
      className="w-56 h-56 md:w-64 md:h-64 mx-auto object-contain bg-white rounded-xl border border-amber-200 p-2"
      data-testid="qr-image"
    />
    <p className="text-sm text-stone-500 mt-2">Scan this QR code with any UPI app to pay {money(s, booking.amount)}</p>
  </div>
)}
              {payMethod === "bank" && (
                <div className="space-y-1 text-sm text-stone-700">
                  <div><strong>A/c Name:</strong> {s.bank_details?.account_name}</div>
                  <div><strong>A/c No:</strong> {s.bank_details?.account_number}</div>
                  <div><strong>IFSC:</strong> {s.bank_details?.ifsc}</div>
                  <div><strong>Bank:</strong> {s.bank_details?.bank}</div>
                </div>
              )}
            </div>

            <label className="block text-sm font-semibold text-stone-700 mb-1">Payment Reference / UTR Number *</label>
 <input
              value={payRef}
              onChange={(e) => {
                const v = e.target.value;
                setPayRef(
                  payMethod === "bank"
                    ? v.replace(/[^A-Za-z0-9]/g, "").slice(0, 22)
                    : v.replace(/\D/g, "").slice(0, 12)
                );
              }}
              inputMode={payMethod === "bank" ? "text" : "numeric"}
              placeholder={payMethod === "bank" ? "Bank reference number" : "12-digit UPI transaction ID"}
              className={`w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-400 ${payRefError ? "border-red-500" : "border-stone-300"}`}
              data-testid="input-payref"
            />
            {payRefError && <p className="text-xs text-red-600 mt-1" data-testid="input-payref-error">{payRefError}</p>}
                        <p className="text-xs text-stone-500 mt-2">Your payment will be <strong>verified by temple staff</strong>. The booking is confirmed once verification is complete.</p>
          </div>
        )}

        {/* STEP 6: Confirm */}
        {step === 6 && booking && (
          <div className="text-center" data-testid="step-confirm">
            <div className="w-20 h-20 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mx-auto mb-4"><PartyPopper className="w-10 h-10" /></div>
            <h2 className="font-display text-2xl font-bold text-stone-900">Booking Received!</h2>
            <p className="text-stone-500 mt-2">Your seva booking has been recorded and is pending payment verification.</p>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mt-6 text-left max-w-md mx-auto">
              <div className="text-center mb-4">
                <div className="text-xs uppercase tracking-widest text-stone-500">Booking ID</div>
                <div className="font-display text-2xl font-extrabold text-orange-700" data-testid="confirm-booking-id">{booking.booking_id}</div>
              </div>
              {[["Devotee", booking.devotee_name], ["Persons", booking.persons || 1], ["Seva", booking.seva_name], ["Date", booking.date], ["Time", booking.time_slot], ["Amount", money(s, booking.amount)], ["Payment", booking.payment_status], ["Status", booking.booking_status?.replace(/_/g, " ")]].map(([k, v]) => (
                <div key={k} className="flex justify-between py-1.5 text-sm"><span className="text-stone-500">{k}</span><span className="font-semibold text-stone-900 capitalize">{v}</span></div>
              ))}
            </div>
            <p className="text-sm text-stone-500 mt-4">Please save your Booking ID. Use it with your contact number to view or cancel your booking anytime.</p>
            <div className="flex flex-wrap gap-3 justify-center mt-6">
              <button onClick={() => window.print()} className="border border-orange-300 text-orange-700 font-bold px-5 py-3 rounded-full hover:bg-amber-100" data-testid="confirm-print">Print / Save Slip</button>
              <Link to="/booking/lookup" className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-5 py-3 rounded-full" data-testid="confirm-lookup">View My Booking</Link>
              <Link to="/" className="text-stone-600 font-semibold px-5 py-3">Back to Home</Link>
            </div>
          </div>
        )}

        {/* NAV BUTTONS */}
        {step < 5 && (
          <div className="flex justify-between mt-8 pt-6 border-t border-amber-100">
            <button onClick={() => setStep((x) => Math.max(0, x - 1))} disabled={step === 0} className="inline-flex items-center gap-1 px-5 py-3 rounded-full font-semibold text-stone-600 disabled:opacity-30" data-testid="booking-back"><ArrowLeft className="w-4 h-4" /> Back</button>
            {step < 4 ? (
              <button onClick={() => { if (step === 1) loadSlots(form.date); setStep((x) => x + 1); }} disabled={!canNext()} className="inline-flex items-center gap-1 bg-orange-600 hover:bg-orange-700 text-white font-bold px-6 py-3 rounded-full disabled:opacity-40 transition-colors" data-testid="booking-next">Next <ArrowRight className="w-4 h-4" /></button>
            ) : (
              <button onClick={createBooking} disabled={submitting} className="inline-flex items-center gap-1 bg-orange-600 hover:bg-orange-700 text-white font-bold px-6 py-3 rounded-full disabled:opacity-40 transition-colors" data-testid="booking-proceed-payment">{submitting ? "Please wait…" : "Proceed to Payment"} <ArrowRight className="w-4 h-4" /></button>
            )}
          </div>
        )}
        {step === 5 && (
          <div className="flex justify-end mt-8 pt-6 border-t border-amber-100">
<button onClick={submitPayment} disabled={submitting || !payRefValid} className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-3 rounded-full disabled:opacity-40 transition-colors" data-testid="booking-submit-payment">{submitting ? "Submitting…" : "Submit Payment"} <Check className="w-4 h-4" /></button>          </div>
        )}
      </div>
    </Section>
  );
}

function Field({ label, value, onChange, testid, type = "text", error }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-stone-700 mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className={`w-full rounded-xl border px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-orange-400 ${error ? "border-red-500" : "border-stone-300"}`} data-testid={testid} />
      {error && <p className="text-xs text-red-600 mt-1" data-testid={`${testid}-error`}>{error}</p>}
    </div>
  );
}

function PayTab({ icon: Icon, label, active, onClick, testid }) {
  return (
    <button onClick={onClick} className={`flex-1 flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${active ? "border-orange-600 bg-amber-50 text-orange-700" : "border-stone-200 text-stone-600"}`} data-testid={testid}>
      <Icon className="w-5 h-5" /><span className="text-xs font-semibold">{label}</span>
    </button>
  );
}
